/**
 * Iter-157A Phase 5 — The Attic page.
 *
 * Trust surface, not a workflow surface. The emotional contract:
 *
 *   "Everything is here."
 *   "Nothing has been lost."
 *   "You can always get back."
 *
 * The page is intentionally calm. There are no dashboards, no charts,
 * no "0 of 12 anchors used" gauges. Just a quiet hero, an honest
 * status chip ("Auto-snapshotting active · last hourly snapshot 14
 * minutes ago"), and a vertical timeline of moments worth keeping —
 * grouped by day so the producer scans their project's life
 * chronologically.
 *
 * For archived projects this page tilts toward READ-AND-RESEARCH
 * rather than WRITE-AND-RESTORE: the live restore is blocked at the
 * backend; the primary action becomes "Restore to a copy" so the
 * frozen archive stays exactly as it was.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Archive, Loader2, Plus, RefreshCw, ShieldCheck, Sparkles, Snowflake } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import ProjectHeader from "../components/ProjectHeader";
import AtticTimeline from "../components/AtticTimeline";
import AtticPreviewDrawer from "../components/AtticPreviewDrawer";
import AtticCloneDialog from "../components/AtticCloneDialog";
import { ArchiveIntegrityChip } from "../components/ArchiveIntegrity";
import { Button } from "../components/ui/button";
import { useRouteBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";


function relativeTime(iso) {
    if (!iso) return "—";
    try {
        const diffMs = Date.now() - new Date(iso).getTime();
        if (diffMs < 0) return "just now";
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
        const days = Math.floor(hrs / 24);
        if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
        const months = Math.floor(days / 30);
        if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
        const years = Math.floor(months / 12);
        return `${years} year${years === 1 ? "" : "s"} ago`;
    } catch (_) { return "—"; }
}


function StatusChip({ project, rows }) {
    const archived = !!project?.archived;
    const lastHourly = useMemo(
        () => (rows || []).find((r) => r.trigger === "auto.hourly"),
        [rows],
    );
    const lastAny = (rows || [])[0];

    if (archived) {
        return (
            <div
                data-testid="attic-status-archived"
                className="rounded-md border border-sky-500/40 bg-sky-500/[0.08] px-3 py-2 text-xs text-sky-700 dark:text-sky-100/90 inline-flex items-center gap-2"
            >
                <Snowflake size={12} className="text-sky-600 dark:text-sky-300" />
                <span>
                    This project is archived — its history is frozen.
                </span>
            </div>
        );
    }
    if (!lastAny) {
        return (
            <div
                data-testid="attic-status-empty"
                className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-neutral-400 inline-flex items-center gap-2"
            >
                <Sparkles size={12} className="text-neutral-500" />
                <span>
                    Auto-saving active. Restore points will fill as you work.
                </span>
            </div>
        );
    }
    return (
        <div
            data-testid="attic-status-active"
            className="rounded-md border border-emerald-500/40 bg-emerald-500/[0.08] px-3 py-2 text-xs text-emerald-700 dark:text-emerald-100/90 inline-flex items-center gap-2"
        >
            <ShieldCheck size={12} className="text-emerald-600 dark:text-emerald-300" />
            <span>
                Auto-snapshotting active
                {lastHourly ? (
                    <>
                        {" · last hourly snapshot "}
                        <span data-testid="attic-status-last-hourly">
                            {relativeTime(lastHourly.created_at)}
                        </span>
                    </>
                ) : (
                    <> · last snapshot {relativeTime(lastAny.created_at)}</>
                )}
            </span>
        </div>
    );
}


function ManualCreateDialog({ open, onClose, onCreate, submitting }) {
    const [label, setLabel] = useState("");
    const [reason, setReason] = useState("");
    useEffect(() => {
        if (open) { setLabel(""); setReason(""); }
    }, [open]);
    return (
        <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose?.()}>
            <DialogContent data-testid="attic-manual-create-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Plus size={16} className="text-amber-200" />
                        Create a restore point
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400 text-sm leading-relaxed">
                        Manual restore points are pinned by default — they stay
                        on this page forever, even if hourly auto-saves roll over.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-1">
                    {submitting ? (
                        <div
                            data-testid="attic-manual-create-banner"
                            className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 flex items-start gap-3"
                        >
                            <Loader2
                                size={14}
                                className="text-amber-200 animate-spin shrink-0 mt-0.5"
                            />
                            <div className="flex-1 text-[12px] text-amber-50 leading-relaxed">
                                <div className="font-semibold">
                                    Saving restore point…
                                </div>
                                <div className="text-amber-100/85 mt-0.5">
                                    Large projects can take a moment. Please keep this
                                    dialog open — the list will update as soon as the
                                    snapshot is ready.
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <div>
                        <label
                            htmlFor="attic-manual-label"
                            className="block text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5"
                        >
                            Give this moment a name
                        </label>
                        <Input
                            id="attic-manual-label"
                            data-testid="attic-manual-label-input"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g. Before pink revision"
                            maxLength={120}
                            disabled={submitting}
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="attic-manual-reason"
                            className="block text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5"
                        >
                            Optional note
                        </label>
                        <Textarea
                            id="attic-manual-reason"
                            data-testid="attic-manual-reason-input"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Future-you will thank you."
                            rows={2}
                            maxLength={400}
                            className="resize-none"
                            disabled={submitting}
                        />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => !submitting && onClose?.()}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        data-testid="attic-manual-create-confirm"
                        onClick={() => onCreate({ label: label.trim(), reason: reason.trim() })}
                        disabled={submitting}
                        className="font-semibold"
                    >
                        {submitting ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" />
                                Saving…
                            </span>
                        ) : "Save restore point"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function AfterRestoreBanner({ result, onDismiss }) {
    if (!result) return null;
    const chain = result.restore_chain || {};
    const undoRpId = chain.pre_restore_rp_id;
    return (
        <div
            data-testid="attic-after-restore-banner"
            className="rounded-xl border border-emerald-500/40 bg-emerald-500/[0.10] px-4 py-3.5 mb-5 flex flex-col sm:flex-row sm:items-center gap-3"
        >
            <div className="flex-1 text-sm text-emerald-800 dark:text-emerald-100/95 leading-relaxed">
                <div className="font-medium" data-testid="attic-after-restore-summary">
                    {result.summary || "Restored."}
                </div>
                <div className="text-xs text-emerald-700/80 dark:text-emerald-100/70 mt-0.5">
                    A safety restore point of the previous state is saved here — undo any time.
                </div>
            </div>
            {undoRpId ? (
                <a
                    href={`#rp-${undoRpId}`}
                    data-testid="attic-after-restore-undo"
                    className="inline-flex items-center justify-center rounded-md border border-emerald-500/50 bg-emerald-500/15 px-3 py-1.5 text-xs font-semibold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/25"
                    onClick={(e) => { e.preventDefault(); onDismiss?.(undoRpId); }}
                >
                    Undo this restore
                </a>
            ) : null}
        </div>
    );
}


export default function AtticPage() {
    const { projectId } = useParams();
    // Iter-158.13.2.20e.4 — apiBase drives whether we call legacy
    // /api/projects/{pid}/restore-points/... or the Route 2
    // Production mount /api/beta/projects/{pid}/restore-points/...
    // ExternalBackupSection follows the same pattern.
    const apiBase = useApiBase();
    const navigate = useNavigate();
    const routeBase = useRouteBase();
    const [project, setProject] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeRp, setActiveRp] = useState(null);
    const [creating, setCreating] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [showCloneStandalone, setShowCloneStandalone] = useState(false);
    const [afterRestore, setAfterRestore] = useState(null);

    const role = project?.role;
    const canManage = role === "owner" || role === "admin";

    const refresh = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const [proj, list] = await Promise.all([
                api.get(`${apiBase}/${projectId}`),
                api.get(`${apiBase}/${projectId}/restore-points`),
            ]);
            // Iter-158.13.2.20e.4 — Hub GET returns {project, script}
            // on Route 2; unwrap so downstream reads (name, role,
            // archived, etc.) resolve regardless of mount.
            const raw = proj.data;
            const flat = (raw && typeof raw === "object" && raw.project && raw.project.id)
                ? raw.project : raw;
            setProject(flat);
            setRows(list.data || []);
        } catch (err) {
            setError(formatApiError(err, "Could not load Restore Points."));
        } finally {
            setLoading(false);
        }
    }, [projectId, apiBase]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleManualCreate = async ({ label, reason }) => {
        // Iter-157P.18.5 — restore-point create resilience. The
        // Meantime project hit a regression where pressing Save
        // showed a brief "saving" indicator then the modal closed
        // and nothing appeared in the list, even after refresh. The
        // underlying cause is the same Cloudflare/origin edge cap
        // that bit revision upload / apply: for large projects the
        // snapshot creation runs >100s, the edge kills the
        // connection, axios throws, the producer's transient toast
        // disappears, and the producer doesn't know whether the
        // backend actually completed.
        //
        // Mirror the Iter-157P.18 polling-bridge pattern:
        //   1. Snapshot the current set of restore-point ids so we
        //      can detect a new arrival even when the POST itself
        //      failed at the edge.
        //   2. Submit with a generous 120s client timeout (wait
        //      past the Cloudflare ~100s window when origin still
        //      responds).
        //   3. On any failure, poll the restore-points list every
        //      5s for up to 3 minutes. If a fresh row appears,
        //      treat as success.
        //   4. Real failure → persistent plain-English error (no
        //      raw Cloudflare wording).
        //
        // Modal stays open across the whole polling window and
        // shows a calm processing state, so the producer can't
        // misread the state as "done" prematurely.
        setCreating(true);
        const knownRpIds = new Set((rows || []).map((r) => r.id));
        const pollForNewRestorePoint = async () => {
            const MAX_ATTEMPTS = 36;  // 36 × 5s = 3 minutes
            const POLL_MS = 5000;
            for (let i = 0; i < MAX_ATTEMPTS; i++) {
                try {
                    const { data } = await api.get(
                        `${apiBase}/${projectId}/restore-points`,
                    );
                    const list = Array.isArray(data) ? data : [];
                    const recovered = list.find((r) => !knownRpIds.has(r.id));
                    if (recovered) return recovered;
                } catch {
                    /* swallow — keep polling */
                }
                await new Promise((resolve) => setTimeout(resolve, POLL_MS));
            }
            return null;
        };
        try {
            await api.post(
                `${apiBase}/${projectId}/restore-points`,
                { label: label || null, reason: reason || null },
                { timeout: 120000 },
            );
            // Iter-157P.18.5 — persistent success toast so producers
            // on mobile cannot miss the confirmation.
            toast.success("Restore point saved.", { duration: 8000 });
            setShowCreate(false);
            await refresh();
        } catch (err) {
            // Polling bridge — the POST may have failed at the edge
            // while the backend kept working. Look for the new row
            // before declaring failure.
            const recovered = await pollForNewRestorePoint();
            if (recovered) {
                toast.success("Restore point saved.", {
                    description: "The save took a moment but the snapshot is now on the list.",
                    duration: 8000,
                });
                setShowCreate(false);
                await refresh();
            } else {
                // True failure. Never the raw Cloudflare/edge text.
                toast.error(
                    "We couldn't save this restore point.",
                    {
                        description:
                            "Your project is safe. Please wait a moment and try again. If it keeps failing, your project may be too large for a single snapshot — contact support.",
                        duration: Infinity,
                    },
                );
            }
        } finally {
            setCreating(false);
        }
    };

    const handleTogglePin = async (rp) => {
        const path = rp.pinned ? "unpin" : "pin";
        try {
            const res = await api.post(
                `${apiBase}/${projectId}/restore-points/${rp.id}/${path}`,
            );
            setRows((prev) =>
                prev.map((r) => (r.id === rp.id ? res.data : r)),
            );
            if (activeRp?.id === rp.id) setActiveRp(res.data);
            toast.success(rp.pinned ? "Unpinned." : "Pinned — kept forever.");
        } catch (err) {
            toast.error(formatApiError(err, "Could not change pin state."));
        }
    };

    const handleAfterRestore = async (result) => {
        setAfterRestore(result);
        setActiveRp(null);
        await refresh();
        // Refresh project state too — name/visibility/archived may have shifted.
        try {
            const proj = await api.get(`${apiBase}/${projectId}`);
            const raw = proj.data;
            const flat = (raw && typeof raw === "object" && raw.project && raw.project.id)
                ? raw.project : raw;
            setProject(flat);
        } catch (_) { /* refresh() already surfaced any error */ }
    };

    const handleUndoLinkClick = (undoRpId) => {
        setAfterRestore(null);
        const target = rows.find((r) => r.id === undoRpId);
        if (target) setActiveRp(target);
    };

    if (loading && !project) {
        return (
            <div className="min-h-screen bg-background text-neutral-100">
                <ProjectHeader />
                <main className="max-w-6xl mx-auto px-4 py-10">
                    <div className="text-sm text-neutral-500 inline-flex items-center gap-2">
                        <Loader2 size={14} className="animate-spin" />
                        Loading restore points…
                    </div>
                </main>
            </div>
        );
    }

    if (!canManage) {
        return (
            <div className="min-h-screen bg-background text-neutral-100">
                <ProjectHeader />
                <main className="max-w-2xl mx-auto px-4 py-10">
                    <div
                        data-testid="attic-not-authorised"
                        className="rounded-xl border border-white/10 bg-card p-6 text-center"
                    >
                        <Archive size={20} className="mx-auto text-neutral-500 mb-2" />
                        <h1 className="text-base font-semibold text-neutral-200">Restore points</h1>
                        <p className="text-sm text-neutral-400 mt-1.5 leading-relaxed">
                            Project history is managed by the Owner and Administrators.
                            Nothing has been lost — your work is captured automatically
                            and they can recover any earlier state.
                        </p>
                    </div>
                </main>
            </div>
        );
    }

    const archived = !!project?.archived;
    const headlineSub = archived
        ? "This project is archived. Its history is preserved. Nothing here will change unless you explicitly create a new project from it."
        : "Everything is here. Nothing has been lost. You can always get back.";

    return (
        <div className="min-h-screen bg-background text-neutral-100">
            <ProjectHeader />
            {/* Iter-158.13.2.20f.5 #6 — responsive shell alignment with the
                rest of the Production Management pages (BackupsPage,
                ProjectSettings, SupervisorNotesPage, SchedulePage all use
                max-w-2xl). Attic used to widen to max-w-6xl on desktop,
                which produced a jarring layout jump on narrow viewports
                where the surrounding pages stayed compact. The desktop
                grid inside is unchanged — max-w-2xl still exceeds the
                single-column pane width, and lg: breakpoints fall back
                naturally on narrow screens. Restore engine untouched. */}
            <main className="max-w-2xl mx-auto px-4 py-6 sm:py-10">
                {/* Hero — calm, no badges, no metrics. */}
                <header className="mb-6 sm:mb-8" data-testid="attic-hero">
                    <div className="flex items-baseline gap-2 text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-1">
                        <Archive size={11} />
                        <span>Restore Points</span>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-100">
                        Project history &amp; restore points
                    </h1>
                    <p className="text-sm text-neutral-400 mt-2 max-w-2xl leading-relaxed">
                        {headlineSub}
                    </p>
                    {/* Iter-157M — Calm "what is this?" explainer so a
                        producer who's never seen this surface before
                        immediately understands the answer to "can I
                        recover mistakes?". Replaces the implementation
                        language that used to lead this page. */}
                    <p className="text-[12px] text-neutral-500 mt-2 max-w-2xl leading-relaxed">
                        Create restore points before major changes, then
                        return to earlier versions of your project if
                        mistakes are made. Restoring never erases history
                        — a safety restore point of the current state is
                        always saved first.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        <StatusChip project={project} rows={rows} />
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            data-testid="attic-refresh"
                            onClick={refresh}
                            disabled={loading}
                            className="text-neutral-400"
                        >
                            <span className="inline-flex items-center gap-1.5">
                                <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                                Refresh
                            </span>
                        </Button>
                    </div>
                    {/* Iter-157B Phase 4 — Archive integrity chip on archived projects. */}
                    {archived ? (
                        <div className="mt-3" data-testid="attic-archive-integrity-wrapper">
                            <ArchiveIntegrityChip
                                projectId={projectId}
                                projectName={project?.name}
                                role={role}
                            />
                        </div>
                    ) : null}
                </header>

                {error ? (
                    <div className="mb-6 rounded-md border border-rose-500/30 bg-rose-500/[0.06] px-3 py-2 text-sm text-rose-200">
                        {error}
                    </div>
                ) : null}

                <AfterRestoreBanner
                    result={afterRestore}
                    onDismiss={handleUndoLinkClick}
                />

                {/* Action bar — different shape for archived vs active. */}
                <div className="mb-6 flex flex-wrap gap-2 items-center">
                    {archived ? (
                        <Button
                            type="button"
                            data-testid="attic-archived-clone-cta"
                            onClick={() => setShowCloneStandalone(true)}
                            disabled={rows.length === 0}
                            className="font-semibold"
                        >
                            Restore to a copy
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            data-testid="attic-create-manual"
                            onClick={() => setShowCreate(true)}
                            className="font-semibold"
                        >
                            <span className="inline-flex items-center gap-2">
                                <Plus size={14} /> Create a restore point
                            </span>
                        </Button>
                    )}
                </div>

                {/* Timeline + preview drawer layout. Iter-158.13.2.20f.5 #6 —
                    dropped the desktop two-column split (`lg:grid-cols-[1fr_22rem]`)
                    now the parent shell is `max-w-2xl`. The preview drawer
                    already opens as an overlay, so a permanent sidebar was
                    never required. Single-column keeps parity with every
                    other Production Management page. */}
                <div className="grid grid-cols-1 gap-6">
                    <div data-testid="attic-timeline-wrapper">
                        {rows.length === 0 ? (
                            <div
                                data-testid="attic-empty-state"
                                className="rounded-xl border border-white/10 bg-card p-8 text-center"
                            >
                                <Archive size={20} className="mx-auto text-neutral-500 mb-2" />
                                <h2 className="text-sm font-medium text-neutral-200">
                                    Nothing captured yet
                                </h2>
                                <p className="text-xs text-neutral-400 mt-1.5 leading-relaxed max-w-md mx-auto">
                                    As you work, OnSetFlow will quietly save restore points
                                    every hour and before any major change. Manual restore
                                    points are pinned forever.
                                </p>
                            </div>
                        ) : (
                            <AtticTimeline
                                rows={rows}
                                activeId={activeRp?.id}
                                onSelect={setActiveRp}
                            />
                        )}
                    </div>

                    <div className="min-w-0">
                        {activeRp ? (
                            <AtticPreviewDrawer
                                project={project}
                                rp={activeRp}
                                onClose={() => setActiveRp(null)}
                                onAfterRestore={handleAfterRestore}
                                onTogglePin={handleTogglePin}
                            />
                        ) : (
                            <div
                                data-testid="attic-preview-empty"
                                className="rounded-xl border border-white/10 bg-card/40 p-5 lg:sticky lg:top-4"
                            >
                                <h3 className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                                    Pick a moment
                                </h3>
                                <p className="text-xs text-neutral-400 leading-relaxed">
                                    Tap any restore point on the timeline to preview what
                                    a restore would do — calmly and in plain English —
                                    before anything actually changes.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <ManualCreateDialog
                    open={showCreate}
                    onClose={() => setShowCreate(false)}
                    onCreate={handleManualCreate}
                    submitting={creating}
                />

                <AtticCloneDialog
                    open={showCloneStandalone}
                    onClose={() => setShowCloneStandalone(false)}
                    project={project}
                    rp={rows[0]}
                    onCloned={(result) => {
                        // Iter-157P.18.4 — also wired up here in the
                        // standalone Attic page flow. The toast above
                        // is already shown by the dialog itself; we
                        // just navigate so the producer lands inside
                        // the restored copy immediately.
                        const newId = result?.new_project_id;
                        if (newId) {
                            navigate(`${routeBase}/${newId}`);
                        }
                    }}
                />
            </main>
        </div>
    );
}
